// Adapted from https://beui.dev/r/collapsible; upstream MIT notice in LICENSE.txt.
// React 18 uses forwarded refs and a string inert attribute. App classes replace
// upstream theme utilities; motion and measured layout retain the approved reveal.
import { ChevronDown } from "lucide-react";
import { motion, useReducedMotion, type HTMLMotionProps } from "motion/react";
import { createContext, forwardRef, useContext, useId, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";

const SPRING_LAYOUT = { type: "spring", stiffness: 360, damping: 32, mass: 0.6 } as const;
const EASE_OUT = [0.16, 1, 0.3, 1] as const;
type CollapsibleContextValue = {
  open: boolean;
  reduce: boolean;
  contentId: string;
  triggerId: string;
  triggerRef: RefObject<HTMLButtonElement>;
  toggle: () => void;
};
const CollapsibleContext = createContext<CollapsibleContextValue | null>(null);

function useCollapsibleContext() {
  const context = useContext(CollapsibleContext);
  if (!context) throw new Error("Collapsible parts must be used inside Collapsible.");
  return context;
}

export function Collapsible({ open, onOpenChange, children, className, ...props }: Omit<HTMLMotionProps<"div">, "layout"> & {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const reduce = useReducedMotion() ?? false;
  const id = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const context = useMemo(() => ({ open, reduce, contentId: `${id}-content`, triggerId: `${id}-trigger`, triggerRef, toggle: () => onOpenChange(!open) }), [id, onOpenChange, open, reduce]);
  return <CollapsibleContext.Provider value={context}>
    <motion.div {...props} layout={!reduce} transition={{ layout: SPRING_LAYOUT }} data-slot="collapsible" data-state={open ? "open" : "closed"} className={`relative w-full ${className ?? ""}`} style={{ originY: 0 }}>
      <motion.div layout={reduce ? false : "position"} transition={{ layout: SPRING_LAYOUT }} style={{ originY: 0 }} className="min-w-0">{children}</motion.div>
    </motion.div>
  </CollapsibleContext.Provider>;
}

export function CollapsibleTrigger({ children, className, onClick, ...props }: Omit<HTMLMotionProps<"button">, "id" | "layout">) {
  const context = useCollapsibleContext();
  return <motion.button {...props} ref={context.triggerRef} type="button" id={context.triggerId} aria-expanded={context.open} aria-controls={context.contentId}
    data-slot="collapsible-trigger" data-state={context.open ? "open" : "closed"}
    onClick={(event) => { onClick?.(event); if (!event.defaultPrevented) context.toggle(); }}
    layout={context.reduce ? false : "position"} transition={{ layout: SPRING_LAYOUT }} className={className}>
    {children}
  </motion.button>;
}

export const CollapsibleContent = forwardRef<HTMLDivElement, Omit<HTMLMotionProps<"div">, "id" | "layout" | "animate" | "initial"> & { contentClassName?: string }>(function CollapsibleContent({ children, contentClassName, className, ...props }, forwardedRef) {
  const context = useCollapsibleContext();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  useLayoutEffect(() => {
    const node = innerRef.current;
    if (!node) return;
    const measure = () => setHeight(node.offsetHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => {
    if (!context.open && contentRef.current?.contains(document.activeElement)) context.triggerRef.current?.focus({ preventScroll: true });
  }, [context.open, context.triggerRef]);
  return <motion.div {...props} ref={(node) => {
    contentRef.current = node;
    if (typeof forwardedRef === "function") forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  }} id={context.contentId} role="region" aria-labelledby={context.triggerId} aria-hidden={!context.open} {...(context.open ? {} : { inert: "" })}
    data-slot="collapsible-content" data-state={context.open ? "open" : "closed"} initial={false} layout={!context.reduce} transition={{ layout: SPRING_LAYOUT }}
    // A one-pixel closed box keeps transform projection continuous at zero.
    style={{ height: context.open ? height || "auto" : 1, originY: 0 }} className={`overflow-hidden ${className ?? ""}`}>
    <motion.div ref={innerRef} layout={!context.reduce} style={{ originY: 0 }} initial={false} animate={{ opacity: context.open ? 1 : 0 }}
      transition={{ opacity: { duration: context.reduce ? 0 : context.open ? 0.18 : 0.12, ease: EASE_OUT }, layout: SPRING_LAYOUT }} className={`flow-root ${contentClassName ?? ""}`}>{children}</motion.div>
  </motion.div>;
});

export function CollapsibleIndicator() {
  const { open, reduce } = useCollapsibleContext();
  // Zero-duration Motion animations still schedule frames; reduced motion must
  // commit the orientation together with the disclosure's expanded state.
  if (reduce) return <span aria-hidden="true" data-slot="collapsible-indicator" style={{ transform: `rotate(${open ? 180 : 0}deg)` }}
    className="ml-auto inline-flex shrink-0 text-mut"><ChevronDown className="w-4 h-4" /></span>;
  return <motion.span aria-hidden="true" data-slot="collapsible-indicator" initial={false} animate={{ transform: `rotate(${open ? 180 : 0}deg)` }}
    transition={SPRING_LAYOUT} className="ml-auto inline-flex shrink-0 text-mut"><ChevronDown className="w-4 h-4" /></motion.span>;
}
